import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Barbaro (manuale V1, rr. 1995-2052).
 *
 * Suddivise in **tre gruppi**:
 *
 * - **3 mosse automatiche** (`type: 'starting'`): *Giramondo*, *Appetiti
 *   Erculei*, *Dominare la Morte*.
 * - **2 Stili di Difesa** (`pickCount: 1`, expandable=false): *Coperto
 *   d'Acciaio*, *Mai Impacciato, Sempre Illeso*. Manuale r. 2023:
 *   «*Inoltre, scegli il tuo stile di difesa:*»
 * - **3 Istinti** (`pickCount: 1`, **expandable=true** — primo uso strutturale
 *   del flag): *Avventura: Un Amante in Ogni Porto*, *Battaglia: Un Fascio di
 *   Muscoli*, *Misticismo: Animale Totemico*. Manuale r. 2033: «*Infine, scegli
 *   un Istinto:*»
 *
 * **Pattern critici**:
 *
 * - ***Appetiti Erculei*** (auto, manuale rr. 2000-2016): mossa-config con
 *   `MoveSchema.choices` su 6 opzioni (distruzione/potere/piaceri/conquista/
 *   ricchezze/fama) e `choiceCount: 2`. Pattern coerente con *Faro di Devozione*
 *   del Paladino, *Nato dalla Terra* del Druido, *Avvelenatore* del Ladro.
 *
 * - ***Dominare la Morte*** (auto, manuale rr. 2018-2020): description-only —
 *   modifica il trigger di *Ultimo Respiro* (regola runtime, pattern coerente
 *   con altre mosse che modificano trigger di base, gestiti dalla logica
 *   applicativa).
 *
 * - ***Misticismo: Animale Totemico*** (Istinto, manuale rr. 2043-2052):
 *   mossa-config con `MoveSchema.choices` su 4 specie (aquila/corvo/lupo/orso)
 *   e `choiceCount: 1`. La specie scelta è il **trigger semantico** per
 *   *Essere il Totem* (mossa avanzata L6-10, description-only, FI-30 — la mossa
 *   concede una mossa specifica di un'altra classe in base alla specie).
 *
 * - ***Mai Impacciato, Sempre Illeso*** è il **gancio** semantico per
 *   l'Armatura derivata: se il PG la sceglie come Stile di Difesa e trasporta
 *   peso < Carico max + non indossa armatura + non imbraccia scudo, +1
 *   Armatura. Regola runtime, description-only nel testo.
 */

const giramondo: Move = {
  id: 'barbaro.giramondo',
  name: 'Giramondo',
  type: 'starting',
  classId: 'barbaro',
  trigger:
    'Quando arrivi in un luogo che hai già visitato o di cui hai sentito parlare (a tua discrezione)',
  description:
    'Chiedi al GM quali sono le tradizioni, i rituali o le convenzioni più importanti di quel posto. Ti dirà ciò che hai bisogno di sapere, onestamente.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const appetitiErculei: Move = {
  id: 'barbaro.appetiti-erculei',
  name: 'Appetiti Erculei',
  type: 'starting',
  classId: 'barbaro',
  trigger: 'Quando devi tirare per una mossa mentre persegui questi appetiti',
  description:
    'Gli altri si possono accontentare di un sorso di vino o di dominare una manciata di servi, ma tu vuoi molto di più! Scegli **due appetiti** dalla lista:\n\n- distruzione pura;\n- potere sugli altri;\n- piaceri della carne;\n- conquista;\n- ricchezze e proprietà;\n- fama e gloria.\n\nQuando **devi tirare per una mossa mentre persegui questi appetiti**, tira 1d6+1d8 invece di 2d6. Se il risultato del d6 è superiore a quello del d8, il GM introdurrà una complicazione o un pericolo derivante dal tuo modo avventato di agire.',
  tags: [],
  prerequisites: [],
  replaces: [],
  choices: [
    { id: 'distruzione-pura', label: 'Distruzione pura' },
    { id: 'potere-sugli-altri', label: 'Potere sugli altri' },
    { id: 'piaceri-della-carne', label: 'Piaceri della carne' },
    { id: 'conquista', label: 'Conquista' },
    { id: 'ricchezze-e-proprieta', label: 'Ricchezze e proprietà' },
    { id: 'fama-e-gloria', label: 'Fama e gloria' },
  ],
  choiceCount: 2,
};

const dominareLaMorte: Move = {
  id: 'barbaro.dominare-la-morte',
  name: 'Dominare la Morte',
  type: 'starting',
  classId: 'barbaro',
  trigger: 'Quando esali il tuo *ultimo respiro*',
  description:
    "Prendi **+1 al tiro**. Inoltre, con un 7–9, sei tu a fare un'offerta alla ✴ Morte in cambio della tua vita. Se la Morte accetta la tua proposta, torni in vita. Se invece la rifiuta, la tua anima sarà sua.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const copertoDAcciaio: Move = {
  id: 'barbaro.coperto-d-acciaio',
  name: "Coperto d'Acciaio",
  type: 'starting',
  classId: 'barbaro',
  trigger: 'Sempre (effetto continuo)',
  description: "Ignori l'etichetta *ingombrante* sull'armatura che indossi.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const maiImpacciatoSempreIlleso: Move = {
  id: 'barbaro.mai-impacciato-sempre-illeso',
  name: 'Mai Impacciato, Sempre Illeso',
  type: 'starting',
  classId: 'barbaro',
  trigger:
    'Finché trasporti un peso inferiore al tuo Carico massimo, e non indossi armatura né imbracci uno scudo',
  description: 'Ottieni **+1 Armatura**.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const istintoAvventura: Move = {
  id: 'barbaro.istinto-avventura',
  name: 'Avventura: Un Amante in Ogni Porto',
  type: 'starting',
  classId: 'barbaro',
  trigger:
    'Quando entri in un luogo civilizzato dove sei già stato in precedenza (a tua discrezione)',
  description:
    "Tira+**CAR**. Con un 10+, c'è una vecchia fiamma disposta ad aiutarti in qualche modo. Con un 7-9, ti aiuterà comunque, ma a un prezzo. Con 6-, le tue disavventure romantiche rendono la vita difficile a te e ai tuoi compagni.\n\nSei già stato qua? Hai lasciato alle tue spalle una scia di cuori infranti? Se la storia che stai forgiando con questi seduti accanto a te può dare una risposta, il gioco è fatto. Se, invece, quelli ti guardano aspettando di conoscere le tue gloriose gesta passate, non puoi certo deluderli! Racconta se hai già calcato queste terre, e se qualcuno ti aspetta speranzoso… o in cerca di vendetta!",
  roll: {
    stat: 'carisma',
    on10plus: { text: "C'è una vecchia fiamma disposta ad aiutarti in qualche modo." },
    on7to9: { text: 'Ti aiuterà comunque, ma a un prezzo.' },
    on6minus: {
      text: 'Le tue disavventure romantiche rendono la vita difficile a te e ai tuoi compagni.',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const istintoBattaglia: Move = {
  id: 'barbaro.istinto-battaglia',
  name: 'Battaglia: Un Fascio di Muscoli',
  type: 'starting',
  classId: 'barbaro',
  trigger: "Quando impugni un'arma",
  description: 'Essa ottiene le etichette *brutale* e *impatto*.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const istintoMisticismo: Move = {
  id: 'barbaro.istinto-misticismo',
  name: 'Misticismo: Animale Totemico',
  type: 'starting',
  classId: 'barbaro',
  trigger: 'Quando richiami il tuo *Animale Totemico* affinché ti presti aiuto',
  description:
    "Quando **prendi questa mossa**, scegli un tipo di animale con cui condividi l'essenza: **aquila**, **corvo**, **lupo**, o **orso**. Puoi comprendere e farti comprendere da tutti gli esemplari della razza che hai scelto.\n\nQuando **richiami il tuo *Animale Totemico* affinché ti presti aiuto**, tira+**SAG**. Con 7+, un esemplare del tipo che hai scelto risponde al tuo richiamo. Con 10+, scegli 2. Con 7-9, scegli 1:\n\n- l'animale è ben disposto nei tuoi confronti;\n- non ci mette molto tempo a raggiungerti;\n- il tuo richiamo non è stato udito da nessun altro.\n\nCon 6-, nessun esemplare della razza del tuo *Animale Totemico* ha sentito il richiamo, oppure un esemplare l'ha sentito e risponderà, ma è il GM a scegliere un'opzione dalla lista.\n\nUn giorno sfiderai anche gli déi… ma per ora devi portare loro rispetto. L'*Animale Totemico* non è uno schiavo che obbedisce senza pensare: ogni esemplare ha la sua personalità, e anche se accorre al tuo richiamo, non è detto sia incline ad aiutarti. Ma sono certo che troverai il modo di convincerlo, magari *parlamentando* con lui, no? In che modo può esserti d'aiuto? Dipende dalla sua natura: un'aquila può pattugliare il territorio dall'alto, un corvo portare un messaggio, un lupo seguire una pista e un orso abbattere una porta.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: 'Un esemplare risponde al tuo richiamo. Scegli 2 opzioni dalla lista.' },
    on7to9: { text: 'Un esemplare risponde al tuo richiamo. Scegli 1 opzione dalla lista.' },
    on6minus: {
      text: "Nessun esemplare risponde, oppure uno risponde ma è il GM a scegliere un'opzione.",
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
  // **Mossa-config**: `MoveSchema.choices` su 4 specie totemiche.
  // La specie scelta (id) è il trigger semantico per *Essere il Totem* (L6-10,
  // description-only, FI-30): aquila→`ladro.io-sparo-per-primo`, corvo→
  // `guerriero.vedere-rosso`, lupo→`paladino.attacco-a-seguire`, orso→
  // `paladino.difensore-devoto`. Vedi `barbaro.essere-il-totem` (L6-10).
  choices: [
    { id: 'aquila', label: 'Aquila' },
    { id: 'corvo', label: 'Corvo' },
    { id: 'lupo', label: 'Lupo' },
    { id: 'orso', label: 'Orso' },
  ],
  choiceCount: 1,
};

/**
 * Mosse iniziali AUTOMATICHE (3): prese tutte al livello 1 senza scelta.
 * *Giramondo*, *Appetiti Erculei* (con 6 `choices`, `choiceCount: 2`),
 * *Dominare la Morte*.
 */
export const barbaroStartingAutoMoves: readonly Move[] = [
  giramondo,
  appetitiErculei,
  dominareLaMorte,
] as const;

/**
 * Stili di Difesa (2): il giocatore ne sceglie UNO al livello 1
 * (`pickCount: 1`, `expandableInAdvanced: false`).
 * *Coperto d'Acciaio* / *Mai Impacciato, Sempre Illeso*.
 */
export const barbaroDefenseStyleMoves: readonly Move[] = [
  copertoDAcciaio,
  maiImpacciatoSempreIlleso,
] as const;

/**
 * Istinti (3): il giocatore ne sceglie UNO al livello 1
 * (`pickCount: 1`, **`expandableInAdvanced: true`** — primo uso strutturale del
 * flag fra le 12 classi DWR).
 * *Avventura* / *Battaglia* / *Misticismo (Animale Totemico)*.
 *
 * La mossa avanzata *Istinti Primordiali* (L2-5, description-only) concede al
 * level-up un altro Istinto del Barbaro non ancora preso — è il "consumatore
 * narrativo" del flag `expandableInAdvanced`. La logica builder che onora il
 * flag arriverà in Fase D (level-up). Tracciato in FI-31 nuovo (pattern
 * trasversale).
 */
export const barbaroInstinctMoves: readonly Move[] = [
  istintoAvventura,
  istintoBattaglia,
  istintoMisticismo,
] as const;

/** Tutte le mosse iniziali (3 auto + 2 Stili + 3 Istinti = 8). */
export const barbaroInitialMoves: readonly Move[] = [
  ...barbaroStartingAutoMoves,
  ...barbaroDefenseStyleMoves,
  ...barbaroInstinctMoves,
];
